/**
 * useCrmProposalTypes - Hook para gerenciar Tipos de Proposta do CRM
 *
 * Fornece listagem + mutations (upsert/delete) via RPCs SECURITY DEFINER:
 *   - crm_list_proposal_types
 *   - crm_upsert_proposal_type
 *   - crm_delete_proposal_type
 *
 * Espelha o padrão já consolidado em useCrmLossReasons (mesma estrutura de
 * cache, toasts e tratamento de erro) para manter consistência no módulo.
 *
 * RELEASE: 2026-08-25 v1.0.0 - Versão inicial.
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('CrmProposalTypes');

/** Estrutura de tipo de proposta retornada pela RPC */
export interface ProposalTypeEntry {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  is_active: boolean;
  sort_order: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Lista os tipos de proposta do tenant.
 * @param includeInactive - Se true, inclui tipos desativados (tela de gestão).
 */
export function useCrmProposalTypes(includeInactive = false) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['crm-proposal-types', tenantId, includeInactive],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('crm_list_proposal_types', {
        p_include_inactive: includeInactive,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao listar tipos de proposta', error);
        throw error;
      }
      return (data ?? []) as ProposalTypeEntry[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const activeTypes = useMemo(
    () => (query.data ?? []).filter((t) => t.is_active),
    [query.data],
  );

  return {
    types: query.data ?? [],
    activeTypes,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export interface UpsertProposalTypeInput {
  id?: string | null;
  name: string;
  description?: string | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

/** Mutation: cria ou atualiza um tipo de proposta */
export function useUpsertProposalType() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (input: UpsertProposalTypeInput) => {
      const { data, error } = await (supabase.rpc as any)('crm_upsert_proposal_type', {
        p_id: input.id ?? null,
        p_name: input.name,
        p_description: input.description ?? null,
        p_color: input.color ?? null,
        p_sort_order: input.sort_order ?? 0,
        p_is_active: input.is_active ?? true,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-proposal-types'] });
      toast.success('Tipo de proposta salvo');
    },
    onError: (err: any) => {
      logger.error('Falha ao salvar tipo de proposta', err);
      toast.error(err?.message || 'Erro ao salvar tipo de proposta');
    },
  });
}

/** Mutation: exclui (ou desativa, quando já utilizado) um tipo de proposta */
export function useDeleteProposalType() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase.rpc as any)('crm_delete_proposal_type', {
        p_id: id,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as {
        mode: 'hard_deleted' | 'soft_deleted';
        proposals_using: number;
      };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['crm-proposal-types'] });
      if (result.mode === 'soft_deleted') {
        toast.success(
          `Tipo desativado — ${result.proposals_using} proposta(s) ainda o utilizam.`,
        );
      } else {
        toast.success('Tipo de proposta excluído');
      }
    },
    onError: (err: any) => {
      logger.error('Falha ao excluir tipo de proposta', err);
      toast.error(err?.message || 'Erro ao excluir tipo de proposta');
    },
  });
}
